import {
  AdapterSource,
  DispatcherAuthPrefix,
  DispatcherCategoryService,
  DispatcherEndpoint,
  DispatcherResource
} from 'modules/types/common';
import { getDispatcherUrl } from 'modules/utils/UrlUtils';
import { DFDHeaders, DFDRequestHeaders } from 'serverTypes/authentication';
import analyticsService, { AnalyticsEvent } from 'services/AnalyticsService';
import { consumerIdSelector, profileConsumerSelector } from 'store/profile/selectors';
import { RootDispatch, RootState, Thunk } from 'store/types';
import * as selectors from './selectors';
import * as types from './types';

const KEY = 'PROFILE';

// shared ui data actions
export const GET_CONSUMER_SHARED_UI_DATA = `${KEY}.GET_CONSUMER_SHARED_UI_DATA`;
export const GET_CONSUMER_SHARED_UI_DATA_SUCCESS = `${KEY}.GET_CONSUMER_SHARED_UI_DATA_SUCCESS`;
export const GET_CONSUMER_SHARED_UI_DATA_FAIL = `${KEY}.GET_CONSUMER_SHARED_UI_DATA_FAIL`;

export const getConsumerSharedUiData = (consumerId: string, useRequestState = true) => (
  dispatch: RootDispatch,
  getStore: () => RootState
) => {
  const state = getStore();
  const profile = profileConsumerSelector(state);
  const { cernerPersonId, consumerId: profileConsumerId } = profile || {};
  if (!cernerPersonId || !profileConsumerId) {
    return;
  }
  const headers: DFDRequestHeaders = {
    [DFDHeaders.consumerId]: profileConsumerId,
    [DFDHeaders.patientId]: cernerPersonId,
    [DFDHeaders.mhpAdapter]: AdapterSource.MHP,
    [DFDHeaders.resourceId]: 'sharedUiData'
  } as DFDRequestHeaders;

  const url = getDispatcherUrl(
    DispatcherCategoryService.CONSUMER,
    DispatcherAuthPrefix.AUTHENTICATED,
    DispatcherResource.UI_DATA,
    DispatcherEndpoint.PARAMETER
  );

  dispatch({
    type: GET_CONSUMER_SHARED_UI_DATA,
    payload: {
      useRequestState,
      requestStateSelector: selectors.consumerUiDataRequestStateSelector,
      client: 'dispatcher',
      request: {
        headers,
        method: 'get',
        url
      }
    }
  });
};

export const UPDATE_CONSUMER_SHARED_UI_DATA = `${KEY}.UPDATE_CONSUMER_SHARED_UI_DATA`;
export const UPDATE_CONSUMER_SHARED_UI_DATA_SUCCESS = `${KEY}.UPDATE_CONSUMER_SHARED_UI_DATA_SUCCESS`;
export const UPDATE_CONSUMER_SHARED_UI_DATA_FAIL = `${KEY}.UPDATE_CONSUMER_SHARED_UI_DATA_FAIL`;

export const updateConsumerSharedUiData = (
  consumerId: string,
  sharedUiData: types.ConsumerSharedUiData
): Thunk => (dispatch, getState) => {
  const headers: DFDRequestHeaders = {
    [DFDHeaders.consumerId]: consumerId,
    [DFDHeaders.mhpAdapter]: AdapterSource.MHP,
    [DFDHeaders.resourceId]: 'sharedUiData'
  } as DFDRequestHeaders;

  const url = getDispatcherUrl(
    '',
    DispatcherAuthPrefix.AUTHENTICATED,
    DispatcherResource.UI_DATA,
    DispatcherEndpoint.PARAMETER
  );

  return dispatch({
    type: UPDATE_CONSUMER_SHARED_UI_DATA,
    payload: {
      client: 'dispatcher',
      request: {
        headers,
        method: 'post',
        url,
        data: {
          ...selectors.sharedUiDataSelector(getState()),
          ...sharedUiData
        }
      }
    }
  });
};

export const UPDATE_CONSUMER_AVATAR = `${KEY}.UPDATE_CONSUMER_AVATAR`;
export const UPDATE_CONSUMER_AVATAR_SUCCESS = `${KEY}.UPDATE_CONSUMER_AVATAR_SUCCESS`;
export const UPDATE_CONSUMER_AVATAR_FAIL = `${KEY}.UPDATE_CONSUMER_AVATAR_FAIL`;

export const updateConsumerAvatar = (
  avatars: types.Avatar[],
  newAvatarId: string | undefined,
  oldAvatarId: string,
  userSelectedConsumerId: string
): Thunk => async (dispatch, getState) => {
  const state = getState();
  const consumerid = consumerIdSelector(state);

  const headers: DFDRequestHeaders = {
    [DFDHeaders.consumerId]: consumerid,
    [DFDHeaders.mhpAdapter]: AdapterSource.MHP,
    [DFDHeaders.resourceId]: 'sharedUiData'
  } as DFDRequestHeaders;

  const url = getDispatcherUrl(
    '',
    DispatcherAuthPrefix.AUTHENTICATED,
    DispatcherResource.UI_DATA,
    DispatcherEndpoint.PARAMETER
  );

  const res = await dispatch({
    type: UPDATE_CONSUMER_AVATAR,
    payload: {
      client: 'dispatcher',
      request: {
        headers,
        method: 'put',
        url,
        data: {
          ...selectors.sharedUiDataSelector(getState()),
          avatars: [...avatars]
        }
      }
    }
  });

  if (!res.error) {
    analyticsService.logEvent(AnalyticsEvent.AvatarSelectionComplete, {
      isProxy: userSelectedConsumerId === consumerid ? 'No' : 'Yes',
      old_avatar_id: oldAvatarId,
      new_avatar_id: newAvatarId || 'Initials'
    });
  }

  return res;
};

export const updateAndRefreshSharedUiData = (
  sharedUiData: types.ConsumerSharedUiData
): Thunk => async (dispatch, getState) => {
  const consumerId = consumerIdSelector(getState());

  if (!consumerId) {
    return 'no consumer id';
  }

  try {
    await dispatch(updateConsumerSharedUiData(consumerId, sharedUiData));
    return dispatch(getConsumerSharedUiData(consumerId, false));
  } catch (err) {
    return err;
  }
};

export const SET_LANGUAGE = `${KEY}.SET_LANGUAGE`;
export const setLanguage = (language: types.LANGUAGE) => ({
  type: SET_LANGUAGE,
  payload: language
});
